import {
    ContentCopyOutlined,
    DeleteOutlined,
    EditOutlined,
    MoreVert,
    Star,
    StarBorder,
    VisibilityOutlined,
} from '@mui/icons-material';
import {
    Alert,
    Checkbox,
    Chip,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import { useState, type MouseEvent } from 'react';

import { FEATURE } from 'constant/features';
import { useHasFeature } from 'hooks/useHasFeature';
import useTranslate from 'hooks/useTranslate';
import type { Template } from 'types';
import { formatRelativeTime } from 'views/retainerAgreement/services/diffService';

const TYPE_LABEL_KEYS: Record<string, string> = {
    'visa-application': 'templateTypeVisaApplication',
    'service-agreement': 'templateTypeServiceAgreement',
    eligibility: 'templateTypeEligibility',
    milestones: 'templateTypeMilestones',
    engagement: 'templateTypeEngagement',
    referral: 'templateTypeReferral',
    subcontractor: 'templateTypeSubcontractor',
    msa: 'templateTypeMsa',
    'revenue-sharing': 'templateTypeRevenueSharing',
    nda: 'templateTypeNda',
    'preferred-partner': 'templateTypePreferredPartner',
};

const PRICING_LABEL_KEYS: Record<string, string> = {
    fixed: 'pricingModelFixed',
    hourly: 'pricingModelHourly',
    milestone: 'pricingModelMilestone',
    hybrid: 'pricingModelHybrid',
};

interface TemplatesTableProps {
    templates: Template[];
    selectedIds: string[];
    onSelectionChange: (ids: string[]) => void;
    onPreview: (template: Template) => void;
    onEdit: (template: Template) => void;
    onDuplicate: (template: Template) => void;
    onDelete: (template: Template) => void;
    onToggleFavorite: (template: Template) => void;
    error?: string | null;
    onClearError?: () => void;
}

export function TemplatesTable({
    templates,
    selectedIds,
    onSelectionChange,
    onPreview,
    onEdit,
    onDuplicate,
    onDelete,
    onToggleFavorite,
    error,
    onClearError,
}: TemplatesTableProps) {
    const { translate } = useTranslate();
    const canManageTemplates = useHasFeature(FEATURE.RETAINER_TEMPLATES);
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
    const [menuTemplate, setMenuTemplate] = useState<Template | null>(null);

    const allSelected = templates.length > 0 && selectedIds.length === templates.length;
    const someSelected = selectedIds.length > 0 && selectedIds.length < templates.length;

    const handleSelectAll = () => {
        onSelectionChange(allSelected ? [] : templates.map((t) => t.id));
    };

    const handleSelectOne = (id: string) => {
        if (selectedIds.includes(id)) {
            onSelectionChange(selectedIds.filter((selectedId) => selectedId !== id));
        } else {
            onSelectionChange([...selectedIds, id]);
        }
    };

    const openMenu = (event: MouseEvent<HTMLElement>, template: Template) => {
        setMenuAnchor(event.currentTarget);
        setMenuTemplate(template);
    };

    const closeMenu = () => {
        setMenuAnchor(null);
        setMenuTemplate(null);
    };

    const runMenuAction = (action: () => void) => {
        action();
        closeMenu();
    };

    return (
        <Stack spacing={2}>
            {error ? (
                <Alert severity="error" onClose={onClearError}>
                    {error}
                </Alert>
            ) : null}

            <TableContainer
                component={Paper}
                variant="outlined"
                sx={{
                    borderRadius: 2,
                    borderColor: 'divider',
                    overflowX: 'auto',
                }}
            >
                <Table size="small" sx={{ minWidth: 960 }}>
                    <TableHead>
                        <TableRow>
                            {canManageTemplates ? (
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        size="small"
                                        checked={allSelected}
                                        indeterminate={someSelected}
                                        onChange={handleSelectAll}
                                        slotProps={{
                                            input: { 'aria-label': translate('selectAll') },
                                        }}
                                    />
                                </TableCell>
                            ) : null}
                            <TableCell>{translate('templateName')}</TableCell>
                            <TableCell>{translate('type')}</TableCell>
                            <TableCell>{translate('countryProgram')}</TableCell>
                            <TableCell>{translate('pricingModel')}</TableCell>
                            <TableCell>{translate('status')}</TableCell>
                            <TableCell>{translate('version')}</TableCell>
                            <TableCell>{translate('updated')}</TableCell>
                            <TableCell align="right">{translate('actions')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {templates.map((template) => {
                            const typeKey = template.type ? TYPE_LABEL_KEYS[template.type] : null;
                            const pricingKey = template.pricingModel
                                ? PRICING_LABEL_KEYS[template.pricingModel]
                                : null;
                            const isSelected = selectedIds.includes(template.id);

                            return (
                                <TableRow key={template.id} hover selected={isSelected}>
                                    {canManageTemplates ? (
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                size="small"
                                                checked={isSelected}
                                                onChange={() => handleSelectOne(template.id)}
                                            />
                                        </TableCell>
                                    ) : null}
                                    <TableCell>
                                        <Stack
                                            direction="row"
                                            spacing={0.75}
                                            sx={{ alignItems: 'center' }}
                                        >
                                            <Typography
                                                component="button"
                                                variant="body2"
                                                onClick={() =>
                                                    canManageTemplates
                                                        ? onEdit(template)
                                                        : onPreview(template)
                                                }
                                                sx={{
                                                    border: 0,
                                                    background: 'none',
                                                    p: 0,
                                                    cursor: 'pointer',
                                                    fontWeight: 600,
                                                    color: 'text.primary',
                                                    textAlign: 'left',
                                                    '&:hover': { color: 'primary.main' },
                                                }}
                                            >
                                                {template.name}
                                            </Typography>
                                            {template.isFavorite ? (
                                                <Star
                                                    sx={{ fontSize: 16, color: 'primary.main' }}
                                                />
                                            ) : null}
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        {typeKey ? (
                                            <Chip size="small" label={translate(typeKey)} />
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                —
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" color="text.secondary">
                                            {template.country || '—'}
                                            {template.program ? ` · ${template.program}` : ''}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        {pricingKey ? (
                                            <Chip
                                                size="small"
                                                variant="outlined"
                                                label={translate(pricingKey)}
                                            />
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                —
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            size="small"
                                            label={translate(
                                                template.status === 'published'
                                                    ? 'published'
                                                    : 'draft'
                                            )}
                                            sx={{
                                                fontWeight: 600,
                                                bgcolor:
                                                    template.status === 'published'
                                                        ? 'rgba(34, 197, 94, 0.12)'
                                                        : 'action.hover',
                                                color:
                                                    template.status === 'published'
                                                        ? '#15803d'
                                                        : 'text.secondary',
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            v{template.currentVersion}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" color="text.secondary">
                                            {formatRelativeTime(template.updatedAt)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Stack
                                            direction="row"
                                            spacing={0.25}
                                            sx={{ justifyContent: 'flex-end' }}
                                        >
                                            {canManageTemplates ? (
                                                <>
                                                    <Tooltip title={translate('edit')}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => onEdit(template)}
                                                            aria-label={translate('edit')}
                                                        >
                                                            <EditOutlined fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title={translate('delete')}>
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => onDelete(template)}
                                                            aria-label={translate('delete')}
                                                        >
                                                            <DeleteOutlined fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </>
                                            ) : null}
                                            <IconButton
                                                size="small"
                                                onClick={(event) => openMenu(event, template)}
                                                aria-label={translate('actions')}
                                            >
                                                <MoreVert fontSize="small" />
                                            </IconButton>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={closeMenu}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <MenuItem
                    onClick={() => menuTemplate && runMenuAction(() => onPreview(menuTemplate))}
                >
                    <ListItemIcon>
                        <VisibilityOutlined fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{translate('preview')}</ListItemText>
                </MenuItem>
                {canManageTemplates ? (
                    <MenuItem
                        onClick={() =>
                            menuTemplate && runMenuAction(() => onDuplicate(menuTemplate))
                        }
                    >
                        <ListItemIcon>
                            <ContentCopyOutlined fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>{translate('duplicate')}</ListItemText>
                    </MenuItem>
                ) : null}
                <MenuItem
                    onClick={() =>
                        menuTemplate && runMenuAction(() => onToggleFavorite(menuTemplate))
                    }
                >
                    <ListItemIcon>
                        {menuTemplate?.isFavorite ? (
                            <Star fontSize="small" />
                        ) : (
                            <StarBorder fontSize="small" />
                        )}
                    </ListItemIcon>
                    <ListItemText>
                        {translate(menuTemplate?.isFavorite ? 'unfavorite' : 'favorite')}
                    </ListItemText>
                </MenuItem>
            </Menu>
        </Stack>
    );
}
